import { useEffect, useRef, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { FiCheck, FiEdit3, FiUserPlus } from 'react-icons/fi';
import { FaTrash } from 'react-icons/fa6';

export interface SearchablePickerOption {
    id: string | number;
    label: string;
    hint?: string;
}

interface Props {
    label: string;
    selectedLabel?: string;
    selectedHint?: string;
    disabled?: boolean;
    allowManualEntry?: boolean;
    manualEntryLabel?: string;
    placeholder?: string;
    emptyMessage?: string;
    minChars?: number;
    floatingResults?: boolean;
    extraActionLabel?: string;
    onExtraAction?: (term: string) => void;
    onSearch: (term: string) => Promise<SearchablePickerOption[]>;
    onSelect: (option: SearchablePickerOption) => void;
    onManualEntry?: (term: string) => void;
    onClear: () => void;
}

/**
 * Search-and-pick field with a removable selection chip.
 *
 * When a value is selected it shows a chip with a trash button; removing the value
 * reopens the search box over the field so the user can look up another option.
 * Manual entry is accepted when `allowManualEntry` is set — the typed term is committed
 * as-is via `onManualEntry`, letting the operator override the catalog value.
 */
const SearchablePicker = ({
    label,
    selectedLabel,
    selectedHint,
    disabled = false,
    allowManualEntry = false,
    manualEntryLabel = 'Usar o texto digitado',
    placeholder = 'Buscar...',
    emptyMessage = 'Nenhum resultado encontrado.',
    minChars = 1,
    floatingResults = false,
    extraActionLabel,
    onExtraAction,
    onSearch,
    onSelect,
    onManualEntry,
    onClear,
}: Props) => {
    const hasSelection = Boolean(selectedLabel);
    const [searching, setSearching] = useState(!hasSelection);
    const [term, setTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [options, setOptions] = useState<SearchablePickerOption[]>([]);
    const [touched, setTouched] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchSeqRef = useRef(0);

    useEffect(() => {
        setSearching(!selectedLabel);
    }, [selectedLabel]);

    useEffect(() => {
        if (searching && !disabled) {
            inputRef.current?.focus();
        }
    }, [searching, disabled]);

    const runSearch = async (value: string) => {
        setTouched(true);
        if (value.trim().length < minChars) {
            setOptions([]);
            return;
        }
        const seq = ++searchSeqRef.current;
        setLoading(true);
        try {
            const result = await onSearch(value);
            if (seq !== searchSeqRef.current) {
                return;
            }
            setOptions(result);
        } finally {
            if (seq === searchSeqRef.current) {
                setLoading(false);
            }
        }
    };

    const handleSelect = (option: SearchablePickerOption) => {
        searchSeqRef.current++;
        onSelect(option);
        setTerm('');
        setOptions([]);
        setTouched(false);
        setSearching(false);
    };

    const handleManualEntry = () => {
        if (!onManualEntry || !term.trim()) return;
        searchSeqRef.current++;
        onManualEntry(term.trim());
        setTerm('');
        setOptions([]);
        setTouched(false);
        setSearching(false);
    };

    const handleExtraAction = () => {
        if (!onExtraAction) return;
        onExtraAction(term.trim());
    };

    const handleClear = () => {
        onClear();
        setSearching(true);
    };

    if (hasSelection && !searching) {
        return (
            <div>
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
                <div className="flex items-center justify-between gap-2 rounded border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-3 py-2">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{selectedLabel}</p>
                        {selectedHint ? (
                            <p className="truncate font-mono text-xs text-blue-600 dark:text-blue-400">{selectedHint}</p>
                        ) : null}
                    </div>
                    {!disabled ? (
                        <button
                            type="button"
                            onClick={handleClear}
                            aria-label={`Remover ${label}`}
                            title="Remover"
                            className="shrink-0 rounded-lg p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 transition-colors"
                        >
                            <FaTrash size={11} />
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div>
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
            <div className={floatingResults ? 'relative' : undefined}>
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        disabled={disabled}
                        value={term}
                        placeholder={placeholder}
                        onChange={(e) => setTerm(e.target.value)}
                        onKeyUp={(e) => {
                            if (e.key === 'Enter') runSearch(term);
                        }}
                        className="block w-full rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-2 pl-9 pr-3 text-sm text-gray-900 dark:text-white focus:border-blue-600 focus:outline-none dark:focus:border-blue-400"
                    />
                    <AiOutlineSearch className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>

                {(loading || options.length > 0 || (touched && !loading) || (allowManualEntry && term.trim()) || onExtraAction) && !disabled ? (
                    <div
                        className={`mt-1 rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm ${
                            floatingResults ? 'absolute left-0 right-0 top-full z-20 shadow-lg' : ''
                        }`}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center py-3">
                                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-500" />
                            </div>
                        ) : (
                            <ul className="max-h-48 overflow-auto text-sm">
                                {options.map((option) => (
                                    <li key={option.id}>
                                        <button
                                            type="button"
                                            onClick={() => handleSelect(option)}
                                            className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-blue-50 dark:hover:bg-blue-900"
                                        >
                                            <span className="truncate">{option.label}</span>
                                            {option.hint ? (
                                                <span className="shrink-0 font-mono text-blue-600 dark:text-blue-400">{option.hint}</span>
                                            ) : null}
                                        </button>
                                    </li>
                                ))}
                                {options.length === 0 && touched ? (
                                    <li className="px-3 py-2 text-center text-xs text-gray-500 dark:text-gray-400">{emptyMessage}</li>
                                ) : null}
                                {allowManualEntry && term.trim() ? (
                                    <li className="border-t border-gray-100 dark:border-slate-700">
                                        <button
                                            type="button"
                                            onClick={handleManualEntry}
                                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700"
                                        >
                                            <FiEdit3 size={13} />
                                            <span className="truncate">{manualEntryLabel}: <span className="font-medium">{term.trim()}</span></span>
                                            <FiCheck size={13} className="ml-auto text-green-600" />
                                        </button>
                                    </li>
                                ) : null}
                                {onExtraAction && touched ? (
                                    <li className="border-t border-gray-100 dark:border-slate-700">
                                        <button
                                            type="button"
                                            onClick={handleExtraAction}
                                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900"
                                        >
                                            <FiUserPlus size={13} />
                                            <span className="truncate">{extraActionLabel || 'Cadastrar novo'}</span>
                                        </button>
                                    </li>
                                ) : null}
                            </ul>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default SearchablePicker;
